import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CalendarDays, Code2, Pencil, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/orgApi";
import { canAccessMainTab, canWriteMainTab } from "@/lib/maintabAccess";
import { fetchPatients, encounterNumberOf, patientNameOf, ENCOUNTER_READ_ONLY_REASON, isEncounterReadOnly } from "@/lib/encounterApi";
import EncounterReadOnlyHint from "@/components/encounter/EncounterReadOnlyHint";
import { ENC } from "@/constants/testIds";
import EncounterForm from "@/components/encounter/EncounterForm";
import FhirViewer from "@/components/organization/FhirViewer";
import "@/styles/emr-encounters.css";

const STATUS_CLASS = {
  planned: "emr-enc__status--planned",
  arrived: "emr-enc__status--arrived",
  triaged: "emr-enc__status--triaged",
  "in-progress": "emr-enc__status--in-progress",
  onleave: "emr-enc__status--onleave",
  finished: "emr-enc__status--finished",
  cancelled: "emr-enc__status--cancelled",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function EncountersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "encounters");
  const canWriteAppointments = canWriteMainTab(user?.maintabAccess, "appointments");
  const canReadPatients = canAccessMainTab(user?.maintabAccess, "patients");
  const [encs, setEncs] = useState([]);
  const [demoIds, setDemoIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [patientFilter, setPatientFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fhirTarget, setFhirTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!canReadPatients) {
      setPatients([]);
      return;
    }
    fetchPatients().then(setPatients);
  }, [canReadPatients]);

  const load = useCallback(async (pid) => {
    setLoading(true);
    try {
      const { data } = await api.get("/encounters", {
        params: pid && pid !== "all" ? { patient: pid } : {},
      });
      setEncs(data.items || []);
      setDemoIds(new Set(data.demoIds || []));
    } catch {
      toast.error("Failed to load encounters.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(patientFilter); }, [patientFilter, load]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/encounters/${deleteTarget.id}`);
      toast.success("Encounter deleted.");
      setDeleteTarget(null);
      load(patientFilter);
    } catch {
      toast.error("Failed to delete.");
    }
  };

  return (
    <div data-testid={ENC.page} className="emr-enc">
      <div className="emr-enc__pagehead">
        <div>
          <h1 data-testid={ENC.title} className="emr-enc__title">
            Encounters
          </h1>
          <p className="emr-enc__subtitle">
            Visits — FHIR <span className="font-fhir">Encounter</span> resource
          </p>
        </div>
        {canWriteAppointments ? (
          <button
            type="button"
            data-testid={ENC.newBtn}
            onClick={() => navigate("/appointments/new")}
            className="btn emr-enc__register"
          >
            <CalendarDays className="emr-enc__register-icon" aria-hidden="true" />
            New Appointment
          </button>
        ) : null}
      </div>

      <div className="emr-enc__filter">
        <Select value={patientFilter} onValueChange={setPatientFilter}>
          <SelectTrigger data-testid={ENC.patientFilter} className="emr-enc__filter-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="emr-enc-select">
            <SelectItem value="all">All patients</SelectItem>
            {patients.map((p) => (
              <SelectItem key={p.id} value={p.id}>{patientNameOf(p)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <section className="card emr-enc__panel">
        {loading ? (
          <div data-testid={ENC.loading} className="emr-enc__status-msg">Loading…</div>
        ) : encs.length === 0 ? (
          <div data-testid={ENC.emptyState} className="emr-enc__empty">
            <CalendarClock className="emr-enc__empty-icon" aria-hidden="true" />
            <p>No encounters yet.</p>
          </div>
        ) : (
          <div className="emr-enc__table-wrap">
            <table data-testid={ENC.table} className="striped highlight emr-enc__table">
              <thead>
                <tr>
                  <th>Encounter #</th>
                  <th>Patient</th>
                  <th>Location</th>
                  <th>Class</th>
                  <th>Status</th>
                  <th>Start</th>
                  <th className="emr-enc__actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {encs.map((r) => (
                  <tr key={r.id} data-testid={`${ENC.row}-${r.id}`}>
                    <td>
                      <Link
                        to={`/encounters/${r.id}`}
                        data-testid={`${ENC.openBtn}-${r.id}`}
                        className="emr-enc__number"
                        title="Open encounter"
                      >
                        {encounterNumberOf(r)}
                      </Link>
                      {demoIds.has(r.id) && (
                        <span data-testid={`demo-badge-${r.id}`} className="emr-enc__demo">DEMO</span>
                      )}
                    </td>
                    <td className="emr-enc__patient">{r.subject?.display || "—"}</td>
                    <td>{r.location?.[0]?.location?.display || (r.class?.code === "VR" ? "Virtual" : "—")}</td>
                    <td>{r.class?.display || r.class?.code || "—"}</td>
                    <td>
                      <span className={`emr-enc__status ${STATUS_CLASS[r.status] || "emr-enc__status--cancelled"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="font-fhir emr-enc__start">{fmt(r.period?.start)}</td>
                    <td>
                      <div className="emr-enc__row-actions">
                        <Button variant="ghost" size="icon" title="View FHIR" className="emr-enc-icon-btn"
                          data-testid={`${ENC.viewFhirBtn}-${r.id}`} onClick={() => setFhirTarget(r)}>
                          <Code2 className="h-4 w-4" />
                        </Button>
                        {canWrite ? (
                          <EncounterReadOnlyHint locked={isEncounterReadOnly(r)}>
                            <Button
                              variant="ghost"
                              size="icon"
                              title={isEncounterReadOnly(r) ? ENCOUNTER_READ_ONLY_REASON : "Edit"}
                              className="emr-enc-icon-btn"
                              data-testid={`${ENC.editBtn}-${r.id}`}
                              disabled={isEncounterReadOnly(r)}
                              onClick={() => { setEditing(r); setFormOpen(true); }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </EncounterReadOnlyHint>
                        ) : null}
                        {canWrite ? (
                          <Button variant="ghost" size="icon" title="Delete" className="emr-enc-icon-btn emr-enc-icon-btn--danger"
                            data-testid={`${ENC.deleteBtn}-${r.id}`} onClick={() => setDeleteTarget(r)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <EncounterForm open={formOpen} editing={editing}
        presetPatientId={patientFilter !== "all" ? patientFilter : undefined}
        onClose={() => setFormOpen(false)} onSaved={() => load(patientFilter)} />

      <FhirViewer open={!!fhirTarget} resource={fhirTarget} onClose={() => setFhirTarget(null)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid={ENC.deleteDialog} className="emr-enc-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete encounter?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes encounter <span className="font-medium">{deleteTarget ? encounterNumberOf(deleteTarget) : ""}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={ENC.confirmDelete} onClick={confirmDelete}
              className="emr-enc-dialog__delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
